// Flow-k (7.2 M1): every Klaviyo flow we follow, as the hourly inventory left
// it — what starts it, Klaviyo's own status, what our engine does with it
// (nothing yet: "ki"; "árnyék" from M3), how many steps and letters, whether
// every part of it was understood, and when it last changed in Klaviyo.
// A row opens the flow step by step; an admin starts a flow of our own here
// (7.3 M7) and edits any flow on its canvas. A flow in shadow shows how it
// compares with Klaviyo (7.2 M4): the 7-day match rate, the unexplained
// differences, and whether it may go live. "Mind árnyékba" switches every flow
// of the country chosen (or of every country) into shadow at once, and "Mind ki"
// back (7.2 M5): the confirmation lists what will switch and what is left out.

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { MoonIcon, PlusIcon, ScaleIcon, SunIcon } from "lucide-react";
import {
  ApiError,
  createFlow,
  fetchFlowComparisonSummary,
  fetchFlowModeCandidates,
  fetchFlows,
  setFlowModes,
} from "@/lib/api";
import type { FlowComparisonSummaryRow, FlowMode, FlowModeCandidate, FlowRow } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLoad } from "@/lib/useLoad";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { DataTable } from "@/components/emc/DataTable";
import { Field } from "@/components/emc/Field";
import { Country } from "@/components/emc/Flag";
import { PageHeader } from "@/components/emc/PageHeader";
import { StatusPill } from "@/components/emc/StatusPill";
import { Toolbar } from "@/components/emc/Toolbar";
import { ActionDialog } from "@/components/emc/ActionDialog";
import { EmptyState, ErrorState } from "@/components/emc/states";
import { count, minute } from "@/components/emc/format";
import { COUNTRIES, triggerText } from "@/pages/flows/model";
import { rateText, rateTone, readyLook } from "@/pages/flows/compareModel";
import { switchAllLabel, switchPlan } from "@/pages/flows/shadowModel";

const REASON_MIN = 3;
const REASON_MAX = 500;

/** "Mind árnyékba" / "Mind ki": every flow of the country (or of every country)
 * the switch takes, listed before anything moves; each is switched and logged
 * on its own, with the admin's reason. Shadow decides, it never sends. */
function SwitchAllDialog({
  target,
  country,
  onOpenChange,
  onDone,
}: {
  target: FlowMode | null;
  country: string;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}) {
  const [flows, setFlows] = useState<FlowModeCandidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (target === null) return;
    let live = true;
    setFlows(null);
    setError(null);
    fetchFlowModeCandidates(target, country)
      .then((answer) => live && setFlows(answer.flows))
      .catch((raised: unknown) => live && setError(raised instanceof Error ? raised.message : "Ismeretlen hiba."));
    return () => {
      live = false;
    };
  }, [target, country]);
  if (target === null) return null;
  const plan = switchPlan(flows ?? []);
  const label = switchAllLabel(target, country);
  return (
    <ActionDialog
      open
      onOpenChange={onOpenChange}
      title={label}
      description={
        target === "shadow" ? (
          <>
            A motor ezeknél a flow-knál ettől a pillanattól minden indító-jelet felvesz, és lépésenként eldönti, kinek
            menne levél, de <strong>levelet nem küld</strong>. A jel-húzás a hiányzó adatfolyamokat magától létrehozza.
          </>
        ) : (
          <>A motor ezeknél nem vesz fel új jelet és nem léptet; a futások és döntések megmaradnak.</>
        )
      }
      confirmLabel={flows === null ? label : `${label}: ${count(plan.count)} flow`}
      reason={{
        label: "Miért kapcsolod át?",
        placeholder: target === "shadow" ? "pl. mind az 57 összevetése a BF előtt" : "pl. a jel-húzás leállt",
        max: REASON_MAX,
      }}
      onConfirm={async (reason) => {
        if (reason.length < REASON_MIN) throw new Error(`Az indoklás legalább ${REASON_MIN} karakter legyen.`);
        if (plan.count === 0) throw new Error("Nincs átkapcsolható flow.");
        const ids = plan.switching.flatMap((group) => group.flows.map((flow) => flow.id));
        let answer;
        try {
          answer = await setFlowModes(ids, target, reason);
        } catch (raised: unknown) {
          throw new Error(raised instanceof ApiError || raised instanceof Error ? raised.message : "Ismeretlen hiba.");
        }
        onDone();
        if (answer.refused.length > 0) {
          throw new Error(
            `${count(answer.switched.length)} átkapcsolva; ${count(answer.refused.length)} nem: ` +
              answer.refused.map((each) => `#${each.flow} (${each.error})`).join(", "),
          );
        }
      }}
    >
      {error !== null && <ErrorState message={error} />}
      {flows === null && error === null && <p className="m-0 text-muted-foreground">Betöltés…</p>}
      {flows !== null && (
        <div className="flex max-h-80 flex-col gap-2 overflow-y-auto text-label">
          {plan.count === 0 ? (
            <p className="m-0 text-muted-foreground">Nincs átkapcsolható flow.</p>
          ) : (
            plan.switching.map((group) => (
              <div key={group.country}>
                <p className="m-0 font-medium">
                  <Country code={group.country} /> · {count(group.flows.length)} flow
                </p>
                <ul className="m-0 pl-5">
                  {group.flows.map((flow) => (
                    <li key={flow.id}>{flow.name}</li>
                  ))}
                </ul>
              </div>
            ))
          )}
          {plan.leftOut.length > 0 && (
            <div>
              <p className="m-0 font-medium">Kimarad ({count(plan.leftOut.length)}):</p>
              <ul className="m-0 pl-5 text-muted-foreground">
                {plan.leftOut.map(({ flow, why }) => (
                  <li key={flow.id}>
                    {flow.country} · {flow.name} — {why}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {plan.already > 0 && (
            <p className="m-0 text-muted-foreground">{count(plan.already)} flow már ebben a módban van.</p>
          )}
        </div>
      )}
    </ActionDialog>
  );
}

/** The flow's comparison with Klaviyo in one cell: the 7-day match rate, the
 * unexplained differences, "élesíthető?". Nothing for a flow not compared. */
function CompareMark({ row }: { row: FlowComparisonSummaryRow | undefined }) {
  const week = row?.windows["7"];
  if (row === undefined || week === undefined || week.days === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  const ready = readyLook(row);
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <StatusPill tone={rateTone(week)} title={`${week.days} végleges nap · ${count(week.compared)} tétel`}>
        {rateText(week)}
      </StatusPill>
      {week.unexplained > 0 && (
        <span className="data-mono text-label text-red-ink">{count(week.unexplained)} eltérés</span>
      )}
      <StatusPill tone={ready.tone} title={ready.title}>
        {ready.label}
      </StatusPill>
    </span>
  );
}

/** "Klaviyóban változott" with its moment, and whether we followed it. */
export function ChangeMark({ row }: { row: Pick<FlowRow, "klaviyo_changed_at" | "change_not_followed"> }) {
  if (row.klaviyo_changed_at === null && !row.change_not_followed) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <span className="flex flex-col items-start gap-0.5">
      <StatusPill
        tone={row.change_not_followed ? "warn" : "scheduled"}
        title={
          row.change_not_followed
            ? "A Klaviyóban megváltozott, de ez a flow nem követi a Klaviyót: az érvényes verzió a régi."
            : "A Klaviyóban megváltozott; az új definíció új verzió lett."
        }
      >
        {row.change_not_followed ? "Klaviyóban változott, nem követve" : "Klaviyóban változott"}
      </StatusPill>
      {row.klaviyo_changed_at !== null && (
        <span className="data-mono text-label text-muted-foreground">{minute(row.klaviyo_changed_at)}</span>
      )}
    </span>
  );
}

/** "igen", or "nem" with how many places were not understood. */
export function SupportMark({ supported, problems }: { supported: boolean | null; problems: number }) {
  if (supported === null) return <span className="text-muted-foreground">—</span>;
  return supported ? (
    <StatusPill tone="good">igen</StatusPill>
  ) : (
    <StatusPill tone="danger" title="Ami nem támogatott, az árnyékban sem futhat.">
      nem · {count(problems)} hely
    </StatusPill>
  );
}

/** A flow of our own: a country and a name; the editor opens on its empty draft. */
function NewFlowDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate();
  const [country, setCountry] = useState("HU");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const made = await createFlow({ country, name: name.trim() });
      onOpenChange(false);
      navigate(`/flowk/${made.id}/vaszon/${made.draft_version ?? 1}`);
    } catch (raised: unknown) {
      setError(raised instanceof Error ? raised.message : "Ismeretlen hiba.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-narrow">
        <DialogHeader>
          <DialogTitle>Új flow</DialogTitle>
          <DialogDescription>
            Saját flow, nem a Klaviyóból: kikapcsolt módban indul, üres vázlattal. Az indítót és a lépéseket a vásznon
            adod meg.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Field label="Ország" htmlFor="new-flow-country">
            <NativeSelect id="new-flow-country" value={country} onChange={(event) => setCountry(event.target.value)}>
              {COUNTRIES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="Név" htmlFor="new-flow-name">
            <Input id="new-flow-name" value={name} maxLength={300} onChange={(event) => setName(event.target.value)} />
          </Field>
          {error && <ErrorState message={error} />}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Mégse
          </Button>
          <Button disabled={busy || name.trim() === ""} onClick={() => void create()}>
            Flow létrehozása
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FlowList() {
  const navigate = useNavigate();
  const isAdmin = useAuth().user?.role === "admin";
  const [creating, setCreating] = useState(false);
  const [switching, setSwitching] = useState<FlowMode | null>(null);
  const [country, setCountry] = useState("");
  const [mode, setMode] = useState("");
  const [changedOnly, setChangedOnly] = useState("");
  const [state, reload] = useLoad(
    () => fetchFlows({ country, mode, changed_since: changedOnly }),
    [country, mode, changedOnly],
  );
  const rows = state.data ?? [];
  // The comparison's figures, joined by flow id; a failed read leaves the column empty.
  const [compared] = useLoad(() => fetchFlowComparisonSummary(country), [country]);
  const comparison = new Map((compared.data?.flows ?? []).map((each) => [each.flow_id, each]));

  const columns: ColumnDef<FlowRow>[] = [
    { id: "country", header: "Ország", cell: ({ row }) => <Country code={row.original.country} /> },
    {
      id: "name",
      header: "Név",
      cell: ({ row }) => (
        <Link
          to={`/flowk/${row.original.id}`}
          className="font-medium text-ink hover:text-blue-ink"
          onClick={(event) => event.stopPropagation()}
        >
          {row.original.name}
          {row.original.draft_version !== null && (
            <StatusPill tone="scheduled" className="ml-2" title="Valaki szerkeszti: nyitott vázlat">
              vázlat ({row.original.draft_version}.)
            </StatusPill>
          )}
        </Link>
      ),
      meta: { wrap: true },
    },
    { id: "trigger", header: "Indító", cell: ({ row }) => triggerText(row.original.trigger), meta: { wrap: true } },
    {
      id: "source",
      header: "Klaviyo-állapot",
      cell: ({ row }) =>
        row.original.source_status === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <StatusPill domain="flowSource" state={row.original.source_status} />
        ),
    },
    { id: "mode", header: "Mód", cell: ({ row }) => <StatusPill domain="flowMode" state={row.original.mode} /> },
    {
      id: "steps",
      header: "Lépések",
      cell: ({ row }) => count(row.original.steps),
      meta: { align: "right", mono: true },
    },
    {
      id: "emails",
      header: "Levelek",
      cell: ({ row }) => count(row.original.emails),
      meta: { align: "right", mono: true },
    },
    {
      id: "supported",
      header: "Támogatott?",
      cell: ({ row }) => <SupportMark supported={row.original.supported} problems={row.original.problems} />,
    },
    { id: "changed", header: "Változás", cell: ({ row }) => <ChangeMark row={row.original} /> },
    {
      id: "compare",
      header: "Klaviyóval · 7 nap",
      cell: ({ row }) => <CompareMark row={comparison.get(row.original.id)} />,
    },
  ];

  // "Csak a változottak": the last 7 days, on the hour, so the list's own
  // reload does not ask a new question every time.
  const sinceWeek = new Date(Math.floor(Date.now() / 3_600_000) * 3_600_000 - 7 * 86_400_000).toISOString();

  return (
    <div>
      <PageHeader
        title="Flow-k"
        description="A Klaviyo élő és kézi flow-i lépésről lépésre, ahogy az óránkénti leltár utoljára látta, és a saját flow-k. Egy flow-ra kattintva a teljes folyamat látszik; a vásznon szerkeszthető."
        actions={
          <>
            <Button asChild variant="outline" size="sm">
              <Link to="/flowk/osszevetes">
                <ScaleIcon />
                Összevetés
              </Link>
            </Button>
            {isAdmin && (
              <>
                <Button variant="outline" size="sm" onClick={() => setSwitching("shadow")}>
                  <MoonIcon />
                  {switchAllLabel("shadow", country)}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setSwitching("off")}>
                  <SunIcon />
                  {switchAllLabel("off", country)}
                </Button>
                <Button size="sm" onClick={() => setCreating(true)}>
                  <PlusIcon />
                  Új flow
                </Button>
              </>
            )}
          </>
        }
      />
      <NewFlowDialog open={creating} onOpenChange={setCreating} />
      <SwitchAllDialog
        target={switching}
        country={country}
        onOpenChange={(open) => !open && setSwitching(null)}
        onDone={reload}
      />
      <Toolbar className="mb-4">
        <Field label="Ország" htmlFor="flow-country">
          <NativeSelect id="flow-country" value={country} onChange={(event) => setCountry(event.target.value)}>
            <option value="">Mind</option>
            {COUNTRIES.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <Field label="Mód" htmlFor="flow-mode">
          <NativeSelect id="flow-mode" value={mode} onChange={(event) => setMode(event.target.value)}>
            <option value="">Mind</option>
            <option value="off">ki</option>
            <option value="shadow">árnyék</option>
          </NativeSelect>
        </Field>
        <Field label="Változás" htmlFor="flow-changed">
          <NativeSelect
            id="flow-changed"
            value={changedOnly === "" ? "" : "week"}
            onChange={(event) => setChangedOnly(event.target.value === "week" ? sinceWeek : "")}
          >
            <option value="">Mind</option>
            <option value="week">Klaviyóban változott az elmúlt 7 napban</option>
          </NativeSelect>
        </Field>
      </Toolbar>
      {state.error !== null && <ErrorState message={state.error} onRetry={reload} className="mb-4" />}
      <DataTable
        columns={columns}
        data={rows}
        getRowId={(row) => String(row.id)}
        onRowClick={(row) => navigate(`/flowk/${row.id}`)}
        loading={state.data === null && state.error === null}
        label="Flow-k"
        empty={
          <EmptyState
            bare
            title="Nincs flow"
            description="Az óránkénti leltár (python -m emc_worker.klaviyo flows inventory --all) még nem futott, vagy a szűrő mindent kizár."
          />
        }
      />
    </div>
  );
}
